/**
 * f12.1: the review screen's reads and its one write (adr-14, design D7).
 *
 * The review key is `['review', cycleId, sortedUnitIds]`; the PATCH
 * invalidates it and the row's comment thread. f12.2: the live stream writes
 * pushed figures under `reviewQueryKey()`; the 2 s refetch while a row is
 * recalculating or the USD conversion is catching up is only the FALLBACK,
 * enabled while the stream has been down for more than 5 s.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError, api } from '@/api/client';
import type { Review, ReviewRow, ReviewRowChange } from '@/api/client';
import { queryKeys } from '@/api/queryKeys';

export const REVIEW_POLL_MS = 2000;
export const VERSION_CONFLICT = 'CLIENT_REVIEW_VERSION_CONFLICT';
export const CYCLE_NOT_EDITABLE = 'CYCLE_NOT_EDITABLE';

/** The review's one query key; the live stream writes pushed figures here (f12.2). */
export const reviewQueryKey = queryKeys.cycle.review;

export function needsPolling(review: Review | undefined): boolean {
  if (!review) return false;
  if (review.totals.conversion_pending) return true;
  return review.units.some((unit) => unit.rows.some((row) => row.pending_lines > 0));
}

export function useReviewUnits(cycleId: string) {
  return useQuery({
    queryKey: queryKeys.cycle.reviewUnits(cycleId),
    queryFn: () => api.listReviewUnits(cycleId),
    enabled: Boolean(cycleId),
  });
}

export function useReview(cycleId: string, unitIds: readonly string[], { poll = true }: { poll?: boolean } = {}) {
  return useQuery({
    queryKey: reviewQueryKey(cycleId, unitIds),
    queryFn: () => api.getReview(cycleId, unitIds),
    enabled: Boolean(cycleId) && unitIds.length > 0,
    refetchInterval: (query) => (poll && needsPolling(query.state.data) ? REVIEW_POLL_MS : false),
  });
}

export function useReviewComments(cycleId: string, row: ReviewRow | null) {
  const businessUnitId = row?.business_unit_id ?? '';
  const clientId = row?.client.id ?? '';
  return useQuery({
    queryKey: queryKeys.cycle.reviewComments(cycleId, businessUnitId, clientId),
    queryFn: () => api.listReviewComments(cycleId, businessUnitId, clientId),
    enabled: Boolean(cycleId && businessUnitId && clientId),
  });
}

/** The current row a version conflict carries, if it carries one. */
export function conflictRow(error: unknown): ReviewRow | null {
  if (!(error instanceof ApiError) || error.reason !== VERSION_CONFLICT) return null;
  const body = error.body as { current?: ReviewRow | null } | null;
  return body?.current ?? null;
}

export interface RowSave {
  row: ReviewRow;
  change: Omit<ReviewRowChange, 'expected_version'>;
}

export function useSaveReviewRow(cycleId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ row, change }: RowSave) =>
      api.updateReviewRow(cycleId, row.business_unit_id, row.client.id, {
        ...change,
        expected_version: row.version ?? null,
      }),
    onSettled: (_data, _error, { row }) => {
      void client.invalidateQueries({ queryKey: ['review', cycleId] });
      void client.invalidateQueries({
        queryKey: queryKeys.cycle.reviewComments(cycleId, row.business_unit_id, row.client.id),
      });
    },
  });
}
